import type { OmMenuItem } from '@owma/kit'

import i18n from 'i18n'
import { confirmDelete } from 'utils/ui'

import * as calendarService from '../modules/CalendarService'
import type EventInstance from '../modules/EventInstance'

/** 반복 일정의 편집·삭제 적용 범위 */
export type EventScope = 'all' | 'future' | 'this'

/** 컨텍스트 메뉴 항목을 만들 때 참조하는 이벤트 셀의 최소 정보 */
export interface EventCellRef {
  instanceId?: string
  isTodo?: boolean
}

/**
 * 이벤트 셀 우클릭 컨텍스트 메뉴 항목 팩토리.
 * 메뉴를 띄우지 않을 셀(할일 등)에는 null 을 반환한다.
 */
export type EventContextMenuItemsGetter = (event: EventCellRef) => OmMenuItem[] | null

/**
 * 반복 일정 인스턴스가 시리즈의 첫 회차인지 여부.
 * 첫 회차에서는 '이후 모든 일정' 이 '전체 일정' 과 같은 결과가 되므로 선택지에서 제외한다.
 */
export const isRecurrenceSeriesStart = (instance: EventInstance): boolean => {
  const recurStart = instance.event?.start
  if (recurStart == null) return true

  const positionDate = instance.exception?.recurrenceid ?? instance.startDate

  return (
    positionDate.getFullYear() === recurStart.getFullYear() &&
    positionDate.getMonth() === recurStart.getMonth() &&
    positionDate.getDate() === recurStart.getDate()
  )
}

/** 반복 일정 범위 선택지 */
export interface RecurrenceScopeOption {
  scope: EventScope
  label: string
}

/**
 * 반복 일정에 적용할 범위 선택지를 만든다.
 * 시리즈 첫 회차에서는 '이후 모든 일정' 이 '전체 일정' 과 같은 결과가 되므로 제외한다.
 */
export const getRecurrenceScopeOptions = (instance: EventInstance): RecurrenceScopeOption[] => [
  { scope: 'this', label: i18n.t('calendar.edit.thisEvent') },
  ...(isRecurrenceSeriesStart(instance)
    ? []
    : [{ scope: 'future' as const, label: i18n.t('calendar.edit.thisAndAllFutureEvents') }]),
  { scope: 'all', label: i18n.t('calendar.edit.allEventsInTheSeries') },
]

/**
 * 일정 삭제 확인 모달을 띄우고 확인 시 지정한 범위로 삭제한다.
 * 내가 주최한 회의는 삭제가 아닌 회의 취소로 안내한다.
 *
 * @param instance 삭제할 이벤트 인스턴스
 * @param deleteType 반복 일정에 적용할 범위. 반복이 아니면 무시된다.
 * @param onDeleted 삭제 요청 후 실행할 콜백 (팝오버 닫기 등)
 */
export const confirmAndDeleteEvent = (
  instance: EventInstance,
  deleteType: EventScope = 'all',
  onDeleted?: () => void,
): void => {
  let contentText = i18n.t('calendar.result.deleteContent')

  if (instance.isRecurring) {
    switch (deleteType) {
      case 'future':
        contentText = i18n.t('calendar.result.deleteFutureContent')
        break
      case 'this':
        contentText = i18n.t('calendar.result.deleteThisContent')
        break
      case 'all':
        contentText = i18n.t('calendar.result.deleteAllContent')
        break
    }
  }

  const isMeetingCancel = instance.isMeeting && instance.isMyEvent

  confirmDelete({
    title: isMeetingCancel
      ? i18n.t('calendar.result.cancelMeetingTitle')
      : i18n.t('calendar.result.deleteTitle'),
    content: isMeetingCancel ? i18n.t('calendar.result.cancelMeetingContent') : contentText,
    okText: i18n.t('common.button.ok'),
    cancelText: i18n.t('common.button.cancel'),
    onOk: () => {
      let deleteEventType: (typeof calendarService.EventDeleteType)[keyof typeof calendarService.EventDeleteType] =
        calendarService.EventDeleteType.ALL

      if (deleteType === 'future') {
        deleteEventType = calendarService.EventDeleteType.AFTER_ALL
      } else if (deleteType === 'this') {
        deleteEventType = calendarService.EventDeleteType.THIS_ONLY
      }

      calendarService.deleteEvent(instance, deleteEventType)
      onDeleted?.()
    },
  })
}

/**
 * 이벤트가 속한 캘린더가 편집 가능한지 여부.
 * 캘린더를 찾지 못하거나 읽기 전용으로 공유받은 경우 편집·삭제를 노출하지 않는다.
 */
export const isEventEditable = (instance: EventInstance): boolean => {
  const parentCalendar = calendarService.findCalendarByEventServerId(instance.serverId)
  return parentCalendar != null && !parentCalendar.isReadOnly
}
